import { useState } from "react"
import { Plus, GripVertical, Pencil, Trash2, CheckCircle2, XCircle, Settings2 } from "lucide-react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useTicketTypes, useCreateTicketType, useUpdateTicketType, useDeleteTicketType } from "@/hooks/use-ticket-types"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import type { TicketPhase, TicketType } from "@/lib/types"

// ── Phase Form Dialog ────────────────────────────────────────────

interface PhaseFormData {
  name: string
  color: string
  is_resolved: boolean
  is_closed: boolean
}

const PHASE_COLORS = [
  "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#F97316",
  "#22C55E", "#14B8A6", "#06B6D4", "#6366F1", "#EF4444",
]

function PhaseFormDialog({
  open,
  onOpenChange,
  initialData,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialData?: PhaseFormData
  onSave: (data: PhaseFormData) => void
}) {
  const [name, setName] = useState(initialData?.name ?? "")
  const [color, setColor] = useState(initialData?.color ?? PHASE_COLORS[0])
  const [isResolved, setIsResolved] = useState(initialData?.is_resolved ?? false)
  const [isClosed, setIsClosed] = useState(initialData?.is_closed ?? false)

  const handleSave = () => {
    if (!name.trim()) return
    onSave({ name: name.trim(), color, is_resolved: isResolved, is_closed: isClosed })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{initialData ? "Edytuj etap" : "Nowy etap"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Nazwa *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-input px-3 py-2 text-sm text-foreground bg-transparent"
              placeholder="Np. Diagnoza"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Kolor</label>
            <div className="flex gap-2">
              {PHASE_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                    color === c ? "border-foreground" : "border-transparent"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={isResolved}
                onChange={(e) => {
                  setIsResolved(e.target.checked)
                  if (e.target.checked) setIsClosed(false)
                }}
                className="rounded"
              />
              <CheckCircle2 size={14} className="text-green-500" />
              Etap rozwiązania
            </label>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={isClosed}
                onChange={(e) => {
                  setIsClosed(e.target.checked)
                  if (e.target.checked) setIsResolved(false)
                }}
                className="rounded"
              />
              <XCircle size={14} className="text-gray-500" />
              Etap zamknięcia
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} size="sm">Anuluj</Button>
          <Button onClick={handleSave} size="sm" disabled={!name.trim()}>Zapisz</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Sortable Phase Item ──────────────────────────────────────────

function SortablePhaseItem({
  phase,
  onEdit,
  onDelete,
}: {
  phase: TicketPhase & { _localId: string }
  onEdit: () => void
  onDelete: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase._localId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-md border px-3 py-2.5"
      {...attributes}
    >
      <button className="cursor-grab touch-none text-muted-foreground" {...listeners}>
        <GripVertical size={16} />
      </button>
      <div className="h-3 w-3 rounded-full" style={{ backgroundColor: phase.color }} />
      <span className="flex-1 text-sm font-medium text-foreground">
        {phase.name}
      </span>
      {phase.is_resolved && (
        <Badge variant="secondary" className="gap-1 text-[10px]">
          <CheckCircle2 size={10} /> Rozwiązanie
        </Badge>
      )}
      {phase.is_closed && (
        <Badge variant="secondary" className="gap-1 text-[10px]">
          <XCircle size={10} /> Zamknięcie
        </Badge>
      )}
      <button onClick={onEdit} className="rounded p-1 transition-colors hover:bg-muted text-muted-foreground">
        <Pencil size={14} />
      </button>
      <button onClick={onDelete} className="rounded p-1 transition-colors hover:bg-muted text-muted-foreground">
        <Trash2 size={14} />
      </button>
    </div>
  )
}

// ── Ticket Type Editor ───────────────────────────────────────────

function TicketTypeEditor({ ticketType }: { ticketType: TicketType }) {
  const updateMutation = useUpdateTicketType()
  const deleteMutation = useDeleteTicketType()

  const [phases, setPhases] = useState<(TicketPhase & { _localId: string })[]>(
    ticketType.phases.map((p) => ({ ...p, _localId: p.id }))
  )
  const [hasChanges, setHasChanges] = useState(false)
  const [phaseDialog, setPhaseDialog] = useState<{
    open: boolean
    editIndex?: number
    initialData?: PhaseFormData
  }>({ open: false })

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = phases.findIndex((p) => p._localId === active.id)
    const newIndex = phases.findIndex((p) => p._localId === over.id)

    setPhases(arrayMove(phases, oldIndex, newIndex))
    setHasChanges(true)
  }

  const handleAddPhase = (data: PhaseFormData) => {
    const newPhase: TicketPhase & { _localId: string } = {
      id: "",
      _localId: `new_${Date.now()}`,
      name: data.name,
      sort_order: phases.length,
      color: data.color,
      is_resolved: data.is_resolved,
      is_closed: data.is_closed,
      required_fields: [],
      created_at: "",
      updated_at: "",
    }
    setPhases([...phases, newPhase])
    setHasChanges(true)
  }

  const handleEditPhase = (index: number, data: PhaseFormData) => {
    const updated = [...phases]
    updated[index] = { ...updated[index], ...data }
    setPhases(updated)
    setHasChanges(true)
  }

  const handleDeletePhase = (index: number) => {
    if (!confirm("Czy na pewno chcesz usunąć ten etap?")) return
    setPhases(phases.filter((_, i) => i !== index))
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        id: ticketType.id,
        data: {
          phases: phases.map((p, idx) => ({
            id: p.id || undefined,
            name: p.name,
            sort_order: idx,
            color: p.color,
            is_resolved: p.is_resolved,
            is_closed: p.is_closed,
            required_fields: p.required_fields,
          })),
        },
      })
      setHasChanges(false)
      toast.success("Etapy zapisane")
    } catch {
      toast.error("Błąd zapisu etapów")
    }
  }

  const handleDeleteType = async () => {
    if (!confirm("Czy na pewno chcesz usunąć ten typ serwisu?")) return
    try {
      await deleteMutation.mutateAsync(ticketType.id)
      toast.success("Typ serwisu usunięty")
    } catch {
      toast.error("Błąd usuwania typu serwisu")
    }
  }

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 size={16} className="text-muted-foreground" />
          <h2 className="text-base font-semibold text-foreground">{ticketType.name}</h2>
          {ticketType.is_default && <Badge variant="secondary" className="text-[10px]">Domyślny</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <>
              <Button variant="outline" size="sm" onClick={() => {
                setPhases(ticketType.phases.map((p) => ({ ...p, _localId: p.id })))
                setHasChanges(false)
              }}>
                Odrzuć
              </Button>
              <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                Zapisz
              </Button>
            </>
          )}
          {!ticketType.is_default && (
            <Button variant="ghost" size="sm" onClick={handleDeleteType} className="text-red-600">
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={phases.map((p) => p._localId)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {phases.map((phase, idx) => (
              <SortablePhaseItem
                key={phase._localId}
                phase={phase}
                onEdit={() => setPhaseDialog({
                  open: true,
                  editIndex: idx,
                  initialData: { name: phase.name, color: phase.color, is_resolved: phase.is_resolved, is_closed: phase.is_closed },
                })}
                onDelete={() => handleDeletePhase(idx)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button
        variant="outline"
        size="sm"
        onClick={() => setPhaseDialog({ open: true })}
        className="gap-1.5"
      >
        <Plus size={14} /> Dodaj etap
      </Button>

      <PhaseFormDialog
        open={phaseDialog.open}
        onOpenChange={(open) => setPhaseDialog({ open })}
        initialData={phaseDialog.initialData}
        onSave={(data) => {
          if (phaseDialog.editIndex !== undefined) {
            handleEditPhase(phaseDialog.editIndex, data)
          } else {
            handleAddPhase(data)
          }
        }}
      />
    </div>
  )
}

// ── Create Ticket Type Dialog ────────────────────────────────────

function CreateTicketTypeDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [name, setName] = useState("")
  const createMutation = useCreateTicketType()

  const handleCreate = async () => {
    if (!name.trim()) return
    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        phases: [
          { name: "Przyjęcie", sort_order: 0, color: "#3B82F6" },
          { name: "Diagnoza", sort_order: 1, color: "#8B5CF6" },
          { name: "W realizacji", sort_order: 2, color: "#F59E0B" },
          { name: "Zakończono", sort_order: 3, color: "#22C55E", is_resolved: true },
          { name: "Zamknięto", sort_order: 4, color: "#6B7280", is_closed: true },
        ],
      })
      toast.success("Typ serwisu utworzony")
      setName("")
      onOpenChange(false)
    } catch {
      toast.error("Błąd tworzenia typu serwisu")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nowy typ serwisu</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <label className="mb-1.5 block text-sm font-medium text-foreground">Nazwa *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-input px-3 py-2 text-sm text-foreground bg-transparent"
            placeholder="Np. Serwis IT"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} size="sm">Anuluj</Button>
          <Button onClick={handleCreate} size="sm" disabled={!name.trim() || createMutation.isPending}>Utwórz</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Main Page ────────────────────────────────────────────────────

export function TicketPhasesPage() {
  const { data, isLoading } = useTicketTypes()
  const ticketTypes = data?.data ?? []
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Etapy serwisu
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Konfiguruj etapy przepływu serwisowego
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} size="sm">
          <Plus size={14} /> Nowy typ serwisu
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      ) : ticketTypes.length === 0 ? (
        <div
          className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-input py-12 text-muted-foreground"
        >
          <p className="text-sm">Brak typów serwisu</p>
          <Button variant="outline" onClick={() => setCreateDialogOpen(true)}>
            <Plus size={16} /> Utwórz typ serwisu
          </Button>
        </div>
      ) : (
        ticketTypes.map((tt) => <TicketTypeEditor key={tt.id} ticketType={tt} />)
      )}

      <CreateTicketTypeDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
    </div>
  )
}
